import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '../Button/index.js'
import { Menu, MenuItem, MenuSeparator } from '../Menu/index.js'
import styles from './CardActions.module.scss'

/**
 * CardActions — the action row of a card (M3.6b), the presentation half of
 * `docs/section-actions.md`. It renders verbs and nothing else: what each verb
 * does to state is decided in `apps/web/src/actions/`, and the only thing this
 * file knows about Gmail is that a link action is a link.
 *
 * Three shapes, because the table has three:
 *
 * - **link** — a Gmail deep link. A real anchor to a new tab with
 *   `rel="noopener"`, so it reads as what it is: leaving for the thread.
 *   Nothing is sent, drafted or changed. With no thread to open, the verb
 *   stays in place, unavailable, and says why.
 * - **command** — one press, one appended correction (the app's problem).
 * - **prompt** — a verb that needs a word first (Delegate asks who has it
 *   now). Selecting it reveals a small labelled field on its own line under
 *   the row, from the row or from the overflow menu alike; there is no
 *   free-text field on a card until a verb asks for one.
 *
 * Actions marked `overflow` move into the Menu primitive — the repairs and
 * the reversible discards, so the row keeps the section's point. A
 * `destructive` action (Dismiss, Dropped) sits last, after a separator, in
 * medium weight and no red (docs/visual-direction.md).
 */
export type CardActionBase = {
  /** The action's id from the table. Rendered as `data-action`, so a test can name it. */
  readonly id: string
  readonly label: string
  readonly disabled?: boolean | undefined
  /** Why it is unavailable. Shown in the row and announced with the control. */
  readonly reason?: string | undefined
  /** The append is in flight: inert and `aria-busy`. */
  readonly busy?: boolean | undefined
  /** A reversible discard: last in the overflow menu, after a separator. */
  readonly destructive?: boolean | undefined
  /** Behind the overflow menu rather than in the row. */
  readonly overflow?: boolean | undefined
}

export type CardLinkAction = CardActionBase & {
  readonly kind: 'link'
  /** The thread to open; null when the source carries no thread id. */
  readonly href: string | null
  /** Called as the tab opens, to record that it did. It must change no state. */
  readonly onSelect?: (() => void) | undefined
}

export type CardCommandAction = CardActionBase & {
  readonly kind: 'command'
  readonly onSelect: () => void
}

export type CardPromptAction = CardActionBase & {
  readonly kind: 'prompt'
  /** The question, as the field's visible label. */
  readonly prompt: string
  readonly placeholder?: string | undefined
  /** The submit button's verb. */
  readonly confirm: string
  readonly onSubmit: (value: string) => void
}

export type CardAction = CardLinkAction | CardCommandAction | CardPromptAction

export type CardActionsProps = {
  readonly actions: readonly CardAction[]
  /** Names the overflow menu, which has no text of its own. */
  readonly overflowLabel?: string | undefined
}

export function CardActions({ actions, overflowLabel = 'More actions' }: CardActionsProps) {
  const [prompting, setPrompting] = useState<string | null>(null)
  const row = actions.filter((a) => a.overflow !== true)
  const menu = actions.filter((a) => a.overflow === true)
  const ordered = [
    ...menu.filter((a) => a.destructive !== true),
    ...menu.filter((a) => a.destructive === true),
  ]
  const separatorAt = menu.filter((a) => a.destructive !== true).length
  const open = prompting === null ? null : (actions.find((a) => a.id === prompting) ?? null)

  return (
    <>
      {row
        // The verb that asked the question steps aside while it is being
        // answered: the field is the verb now, and two controls with one name
        // is one too many.
        .filter((action) => action.id !== prompting)
        .map((action) => (
          <RowAction key={action.id} action={action} onPrompt={() => setPrompting(action.id)} />
        ))}
      {menu.length > 0 ? (
        <Menu
          label={overflowLabel}
          trigger={
            <Button variant="quiet" size="sm" aria-label={overflowLabel}>
              More
            </Button>
          }
        >
          {ordered.map((action, index) => (
            <MenuFragment
              key={action.id}
              action={action}
              separator={index === separatorAt && separatorAt > 0}
              onPrompt={() => setPrompting(action.id)}
            />
          ))}
        </Menu>
      ) : null}
      {open !== null && open.kind === 'prompt' ? (
        <PromptForm
          action={open}
          onClose={() => setPrompting(null)}
          onSubmit={(value) => {
            setPrompting(null)
            open.onSubmit(value)
          }}
        />
      ) : null}
    </>
  )
}

/** One action in the row: an anchor for a link, a button for everything else. */
function RowAction({ action, onPrompt }: { action: CardAction; onPrompt: () => void }) {
  const reasonId = useId()
  const unavailable = action.disabled === true || (action.kind === 'link' && action.href === null)
  const describe = unavailable && action.reason !== undefined

  if (action.kind === 'link' && action.href !== null && !unavailable)
    return (
      <a
        className={styles.link}
        href={action.href}
        target="_blank"
        rel="noopener"
        data-action={action.id}
        onClick={action.onSelect}
      >
        {action.label}
      </a>
    )

  return (
    <>
      <Button
        variant="quiet"
        size="sm"
        data-action={action.id}
        disabled={unavailable}
        loading={action.busy ?? false}
        aria-describedby={describe ? reasonId : undefined}
        onClick={() => {
          if (action.kind === 'command') action.onSelect()
          if (action.kind === 'prompt') onPrompt()
        }}
      >
        {action.label}
      </Button>
      {describe ? (
        <span id={reasonId} className={styles.reason}>
          {action.reason}
        </span>
      ) : null}
    </>
  )
}

/** One action in the overflow menu, with the separator that precedes the discards. */
function MenuFragment({
  action,
  separator,
  onPrompt,
}: {
  action: CardAction
  separator: boolean
  onPrompt: () => void
}) {
  const unavailable = action.disabled === true || (action.kind === 'link' && action.href === null)
  return (
    <>
      {separator ? <MenuSeparator /> : null}
      <MenuItem
        disabled={unavailable}
        reason={action.reason}
        destructive={action.destructive}
        onSelect={() => {
          if (action.kind === 'command') action.onSelect()
          if (action.kind === 'prompt') onPrompt()
          if (action.kind === 'link' && action.href !== null)
            window.open(action.href, '_blank', 'noopener')
        }}
      >
        {action.label}
      </MenuItem>
    </>
  )
}

/**
 * The one field a card may show: the question a verb asked, on its own line
 * under the row. Escape and Cancel close it without appending anything.
 */
function PromptForm({
  action,
  onClose,
  onSubmit,
}: {
  action: CardPromptAction
  onClose: () => void
  onSubmit: (value: string) => void
}) {
  const fieldId = useId()
  const field = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')

  useEffect(() => {
    field.current?.focus()
  }, [])

  return (
    <form
      className={styles.prompt}
      data-prompt={action.id}
      onSubmit={(event) => {
        event.preventDefault()
        const trimmed = value.trim()
        if (trimmed.length > 0) onSubmit(trimmed)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
    >
      <label className={styles.promptLabel} htmlFor={fieldId}>
        {action.prompt}
      </label>
      <div className={styles.promptRow}>
        <input
          ref={field}
          id={fieldId}
          className={styles.field}
          type="text"
          autoComplete="off"
          placeholder={action.placeholder}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <Button type="submit" variant="secondary" size="sm" disabled={value.trim().length === 0}>
          {action.confirm}
        </Button>
        <Button variant="quiet" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
